/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/anchor-is-valid */

import React,{FC, useEffect, useState} from "react";
import { Card, Dropdown, Spinner, Row, Col, Pagination, Form, Button  } from "react-bootstrap";
import { AVAILABLE_YEARS, HS_DIGITS } from "../../../../libs/constant";
import { getCommodities, getHSCodeDescription, getListGlobalTradeData } from "../core/_request";
import { createColumnHelper, flexRender, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, PaginationState, RowData, SortingState, useReactTable} from '@tanstack/react-table';
import useAxiosPrivate from "../../../../libs/useAxiosPrivate";
import { shortenLargeNumber } from "../../../../libs/misc";
import { TradeDataTable } from "../core/_models";
import { useAuth } from "../../../../modules/auth";


type TopCommodityTableCardProps = {
  handleHSClicked:(hs:string, year:number, flow:string, hsList:string|null, description:string)=>void;
}

const columnHelper = createColumnHelper<TradeDataTable>()

const TopCommodityTableCard:FC<TopCommodityTableCardProps> = ({
  handleHSClicked
}) => {
  const axiosPrivate = useAxiosPrivate()
  const { currentUser } = useAuth();
  const reporterISO3User = currentUser?.economy;
  const [isLoading, setIsLoading] = useState(false)
  const [selectedYear, setSelectedYear] = useState(AVAILABLE_YEARS[0])
  const [selectedHSDigit, setSelectedHSDigit] = useState(HS_DIGITS[HS_DIGITS.length-1])
  const [exportDataTable, setExportDataTable] = useState<TradeDataTable[]>()
  const [importDataTable, setImportDataTable] = useState<TradeDataTable[]>()
  const [pagination, setPagination] = React.useState<PaginationState>({
      pageIndex: 0,
      pageSize: 20,
    })
  const [globalFilter, setGlobalFilter] = useState<any>([])
  const [sorting, setSorting] = React.useState<SortingState>([])
  const columns = [
    columnHelper.accessor('hs',{
        header: +selectedHSDigit.value===1?'HS Section':'HS Code',
        cell: info=>{
          const flow = info.row.original.flow
          const hsList = info.row.original?.hs_list || null
          const hs = info.getValue()
          const description = info.row.original?.description || ''
          return (
            <span 
              className="badge cursor-pointer text-primary fw-1000 " 
              style={{background:'rgba(5, 66, 148, 0.1)'}} 
              title={info.row.original?.hs_list!=='-'?`Group of: ${info.row.original?.hs_list}`:''}
              onClick={() => handleHSClicked(hs,selectedYear,flow, hsList, description)}>
              {hs}
            </span>
            )
        },
        meta: {
          headerClass: 'ps-4 min-w-90px bg-light rounded-start',
          cellClass: 'text-end'
        },
      }),
      columnHelper.accessor('description',{
        header: 'Description',
        cell: info=>info.getValue(),
        meta: {
          headerClass: 'min-w-125px bg-light',
          cellClass: 'text-end'
        },
      }),
      columnHelper.accessor('val',{
        header: "Value",
        cell: info=>shortenLargeNumber(info.getValue()),
        meta: {
          headerClass: 'min-w-90px text-end rounded-end px-3 bg-light',
          cellClass: 'text-end'
        },
      })
  ];

  const exportTable = useReactTable({
    data:exportDataTable || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    state: {
      pagination,
      globalFilter,
      sorting
    },
  })
  const importTable = useReactTable({
    data:importDataTable || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    state: {
      pagination,
      globalFilter,
      sorting
    },
  })

  function paginationRange(dataTable) {
    const currentPage = pagination.pageIndex + 1;
    const totalPages = dataTable.getPageCount();
    const maxPagesToShow = 1;
    const halfRange = Math.floor(maxPagesToShow / 2);

    // Ensure startPage and endPage are within valid range
    let startPage = Math.max(1, currentPage - halfRange);
    let endPage = Math.min(totalPages, currentPage + halfRange);

    // Adjust range to maintain maxPagesToShow
    if (endPage - startPage + 1 < maxPagesToShow) {
      if (currentPage - startPage >= halfRange) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      } else {
        endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
      }
    }

    const range:any = [];

    // Add first page and ellipsis if necessary
    if (startPage > 1) {
      range.push(1);
      if (startPage > 2) {
        range.push('...');
      }
    }

    // Add pages in the calculated range
    for (let i = startPage; i <= endPage; i++) {
      range.push(i);
    }

    // Add last page and ellipsis if necessary
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        range.push('...');
      }
      range.push(totalPages);
    }

    return range;
  }

  const handlePageChange = (page, dataTable) => {
    const currentPage = pagination.pageIndex+1
    const totalPages = dataTable.getPageCount()
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      dataTable.setPageIndex(page-1)
    }
  };

  const handleDisplayPageChange = (e) =>{
    setPagination({
      ...pagination,
      pageIndex: 0,
      pageSize: +e.target.value
    })
  }

  const pagesExportTable = paginationRange(exportTable);
  const pagesImportTable = paginationRange(importTable);
  const totalCountExportTable = exportTable.getRowCount()
  const totalCountImportTable = importTable.getRowCount()

  const generateTableData = (flow) => {
    setExportDataTable([])
    setImportDataTable([])
    getCommodities(axiosPrivate,{
      rep: reporterISO3User,
      par:'WLD', 
      tradeflow:flow,
      hsDigits:+selectedHSDigit.value, 
      year:selectedYear
    }).then(res=>{
      const tradeData = res.data.data.rows
      return tradeData.map(trade=>{
        return {
          hs: trade.hs,
          description: trade.description,
          val: trade.total_value,
          flow,
          hs_list:trade.hs_list || '-'
        }
      })
      })
      .then(dataTable=>{
        if(flow==='exports') setExportDataTable(dataTable)
        if(flow==='imports') setImportDataTable(dataTable)
        setIsLoading(false)
      })
  }
  useEffect(() => {
    setIsLoading(true)
    generateTableData('exports')
    generateTableData('imports')    
    //Reset Tab after re-render
    // Select the tab elements
    const tab1 = document.getElementById('kt_tab_pane_4_header');
    const tab2 = document.getElementById('kt_tab_pane_5_header');

    // Remove active class from both tabs
    if (tab1 && tab2) {
      tab1.classList.remove('active');
      tab2.classList.remove('active');

      // Add active class to the first tab
      tab1.classList.add('active');
    }
  }, [selectedYear, selectedHSDigit])

  const handleYearChange = (year) =>{
    setSelectedYear(+year)    
  }

  const handleHSDigitChange = (hsDigit) =>{
    setSelectedHSDigit(hsDigit)
  }

  const displayHeader = exportTable.getHeaderGroups().map(headerGroup=>(
      <tr className='fw-bold text-gray-800 py-4' key={headerGroup.id}>
      {
        headerGroup.headers.map(header=>{
          return (
            <th key={header.id} className={header.column.columnDef.meta?.headerClass}>
              <div 
                className={header.column.getCanSort()
                    ? 'cursor-pointer select-none'
                    : ''
                }
                title={
                  header.column.getCanSort()
                    ? header.column.getNextSortingOrder() === 'asc'
                      ? 'Sort ascending'
                      : header.column.getNextSortingOrder() === 'desc'
                        ? 'Sort descending'
                        : 'Reset'
                    : undefined
                }
                onClick={header.column.getToggleSortingHandler()}>
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                <span className="ms-2"> 
                  {{
                    asc: (<i className="fa-solid fa-sort-up"></i>),
                    desc: (<i className="fa-solid fa-sort-down"></i>),
                    false: (<i className="fa-solid fa-sort"></i>),
                  }[header.column.getIsSorted() as string] ?? null}
                </span>
                </div>
            </th>
          )
        })
      }
      </tr>
    ))

  return (
  <Card className="card-custom">
    <Card.Header className="card-header-stretch d-flex align-items-center justify-content-between">
      <ul className="nav nav-tabs nav-line-tabs nav-line-tabs-2x nav-stretch fs-6 border-0">
        <li className="nav-item">
          <a
            className="nav-link active"
            data-bs-toggle="tab"
            href="#kt_tab_pane_4"
            id="kt_tab_pane_4_header"
          >
            <i className="bi bi-box-arrow-right"></i>&nbsp;Top Export
          </a>
        </li>
        <li className="nav-item">
          <a
            className="nav-link"
            data-bs-toggle="tab"
            href="#kt_tab_pane_5"
            id="kt_tab_pane_5_header"
          >
            <i className="bi bi-box-arrow-in-left"></i>&nbsp;Top Import
          </a>
        </li>
      </ul>
      <div className="d-flex align-items-center justify-content-between gx-5">
        <Dropdown className="me-1">
          <Dropdown.Toggle variant="label-brand" id="" className="btn btn-sm btn-bold btn-label-brand dropdown-toggle fw-bold">
            {selectedHSDigit.label}
          </Dropdown.Toggle>

          <Dropdown.Menu>
            {
              HS_DIGITS.map(digit=>{
                return (<Dropdown.Item key={digit.value} onClick={() => handleHSDigitChange(digit)}>{digit.label}</Dropdown.Item>)
              })
            }
          </Dropdown.Menu>
        </Dropdown>
        <Dropdown>
          <Dropdown.Toggle variant="label-brand" id="" className="btn btn-sm btn-bold btn-label-brand dropdown-toggle fw-bold dss_graph_trade_partners_available_years_latest">
            {selectedYear}
          </Dropdown.Toggle>

          <Dropdown.Menu>
            {
              AVAILABLE_YEARS.map(year=>{
                return (<Dropdown.Item key={year} onClick={() => handleYearChange(year)}>{year}</Dropdown.Item>)
              })
            }
          </Dropdown.Menu>
        </Dropdown>
      </div>
    </Card.Header>
    <Card.Body style={{height:'600px'}}>
      {!isLoading && importDataTable?.length && exportDataTable?.length ? (
        <div className="tab-content" id="myTabContent">
          <div
            className="tab-pane fade show active"
            id="kt_tab_pane_4"
            role="tabpanel"
          >
            <div className='table-responsive table-fixhead'>
              <table className='table table-row-bordered table-hover align-middle gs-0'>
                <thead>
                  {displayHeader}
                </thead>
                <tbody className="gy-1 overflow-auto">
                  {exportTable.getRowModel().rows.map(row => (
                    <tr key={row.id}>
                        <td key={row.getVisibleCells()[0].id} className="text-center">
                          {flexRender(
                            row.getVisibleCells()[0].column.columnDef.cell, 
                            row.getVisibleCells()[0].getContext())
                          }
                        </td>
                        <td key={row.getVisibleCells()[1].id} className="text-start">
                          {flexRender(
                            row.getVisibleCells()[1].column.columnDef.cell, 
                            row.getVisibleCells()[1].getContext())
                          }
                        </td>
                        <td key={row.getVisibleCells()[2].id} className="text-end fw-bold px-5">
                          {flexRender(
                            row.getVisibleCells()[2].column.columnDef.cell, 
                            row.getVisibleCells()[2].getContext())
                          }
                        </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Row className='my-5' style={{ fontSize: '0.8em' }}>
              <Col md={4} className='d-flex justify-content-start align-items-center'>
                Display
                <Form.Select
                  size="sm"
                  className="w-80px mx-2"
                  onChange={handleDisplayPageChange}
                  value={pagination.pageSize}>
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </Form.Select>
                results
              </Col>
              <Col md={8} className='d-flex justify-content-end'>
                <Pagination size="sm">
                  <Pagination.Prev
                    onClick={() => exportTable.previousPage()}
                    disabled={!exportTable.getCanPreviousPage()} />
                  {/* Start Dynamic Page */}
                  {pagesExportTable.map((page, index) => {
                    if (page === '...') {
                      return (
                        <Pagination.Ellipsis key={index} />
                      );
                    }
                    return (
                      <Pagination.Item
                        key={index}
                        active={page === pagination.pageIndex + 1}
                        onClick={() => handlePageChange(page, exportTable)}
                      >
                        {page}
                      </Pagination.Item>
                    );
                  })}
                  {/* End Dynamic Page */}
                  <Pagination.Next
                    onClick={() => exportTable.nextPage()}
                    disabled={!exportTable.getCanNextPage()} />
                </Pagination>
              </Col>
            </Row>
          </div>
          <div
            className="tab-pane fade"
            id="kt_tab_pane_5"
            role="tabpanel"
          >
            <div className='table-responsive table-fixhead'>
              <table className='table table-row-bordered table-hover align-middle gs-0'>
                <thead>
                  {displayHeader}
                </thead>
                <tbody className="gy-1 overflow-auto">
                  {importTable.getRowModel().rows.map(row => (
                    <tr key={row.id}>
                        <td key={row.getVisibleCells()[0].id} className="text-center">
                          {flexRender(
                            row.getVisibleCells()[0].column.columnDef.cell, 
                            row.getVisibleCells()[0].getContext())
                          }
                        </td>
                        <td key={row.getVisibleCells()[1].id} className="text-start">
                          {flexRender(
                            row.getVisibleCells()[1].column.columnDef.cell, 
                            row.getVisibleCells()[1].getContext())
                          }
                        </td>
                        <td key={row.getVisibleCells()[2].id} className="text-end fw-bold px-5">
                          {flexRender(
                            row.getVisibleCells()[2].column.columnDef.cell, 
                            row.getVisibleCells()[2].getContext())
                          }
                        </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Row className='my-5' style={{ fontSize: '0.8em' }}>
              <Col md={4} className='d-flex justify-content-start align-items-center'>
                Display
                <Form.Select
                  size="sm"
                  className="w-80px mx-2"
                  onChange={handleDisplayPageChange}
                  value={pagination.pageSize}>
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </Form.Select>
                results
              </Col>
              <Col md={8} className='d-flex justify-content-end'>
                <Pagination size="sm">
                  <Pagination.Prev
                    onClick={() => importTable.previousPage()}
                    disabled={!importTable.getCanPreviousPage()} />
                  {/* Start Dynamic Page */}
                  {pagesImportTable.map((page, index) => {
                    if (page === '...') {
                      return (
                        <Pagination.Ellipsis key={index}/>
                      );
                    }
                    return (
                      <Pagination.Item
                        key={index}
                        active={page === pagination.pageIndex + 1}
                        onClick={() => handlePageChange(page, importTable)}
                      >
                        {page}
                      </Pagination.Item>
                    );
                  })}
                  {/* End Dynamic Page */}
                  <Pagination.Next
                    onClick={() => importTable.nextPage()}
                    disabled={!importTable.getCanNextPage()} />
                </Pagination>
              </Col>
            </Row>
          </div>
        </div> 
      ):(
        <div className="text-center">
          <Spinner animation="border" role="status" variant="primary"></Spinner>
        </div>
      )}
    </Card.Body>
  </Card>
  )
}

export {TopCommodityTableCard}
